import React, {useEffect, useState, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {useRewards} from '@/hooks/useRewards';
import {
  RewardSetupCard,
  RewardConditionsCard,
  SegmentsCard,
  ProductsCard,
  DigitalCodesCard
} from './ui-components';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import {fetchAndEnhanceProducts} from './ui-components/utils';

/**
 * RewardSetup component displays the setup step for creating a reward.
 * Contains three cards: Reward Setup, Reward Conditions, and Segments.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onSaveDraft - Handler for save draft action
 * @param {string} [props.mode='create'] - Flow mode: 'create' or 'edit' (view mode is only for step 4)
 * @returns {JSX.Element} The rendered RewardSetup component.
 */
const RewardSetup = ({onSaveDraft, mode = 'create'}) => {
  const {
    formData,
    validation,
    rewardTypes,
    rewardCardTypes,
    segments,
    rewardTypesLoading,
    segmentsLoading,
    bonusProductsLoading,
    rewardCardTypesLoading,
    pepVouchersOrProductsLoading,
    handleFormChange,
    handleFetchedProductsUpdate,
    handleValidation,
    resetRewardConditions,
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    validateDigitalValue,
    validateDigitalSupplier,
    validateLoyaltyURL
  } = useRewards();

  // Local state for validation errors
  const [nameError, setNameError] = useState('');
  const [pointsError, setPointsError] = useState('');
  const [orderQtyError, setOrderQtyError] = useState('');
  const [limitOfProductQtyError, setLimitOfProductQtyError] = useState('');
  const [redeemPointsError, setRedeemPointsError] = useState('');
  const [rewardCashAmountError, setRewardCashAmountError] = useState('');
  const [cardTypeError, setCardTypeError] = useState('');
  const [valueError, setValueError] = useState('');
  // Digital-specific errors
  const [digitalCodeAmountError, setDigitalCodeAmountError] = useState('');
  const [digitalCodePartnerError, setDigitalCodePartnerError] = useState('');
  // Loyalty-specific errors
  const [loyaltyUrlError, setLoyaltyUrlError] = useState('');

  // Local state for product fetching
  const [productsLoading, setProductsLoading] = useState(false);

  // Use ref to store the latest handleFetchedProductsUpdate to avoid including it in dependency array
  // This ensures the effect runs when selectedProducts changes, even though handleFetchedProductsUpdate is memoized
  const handleFetchedProductsUpdateRef = useRef(handleFetchedProductsUpdate);
  useEffect(() => {
    handleFetchedProductsUpdateRef.current = handleFetchedProductsUpdate;
  }, [handleFetchedProductsUpdate]);

  // Effect to fetch products when selectedProducts changes
  useEffect(() => {
    fetchAndEnhanceProducts(
      formData.selectedProducts,
      handleFetchedProductsUpdateRef.current,
      setProductsLoading,
      () => {}, // Empty function for error handling since we're not using it
      formData.fetchedProducts // Pass existing fetched products to preserve details
    );
    // Note: handleFetchedProductsUpdate is excluded from dependencies and accessed via ref to ensure
    // the effect runs when selectedProducts changes, regardless of handleFetchedProductsUpdate reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedProducts]);

  // Helper function to validate a single field
  const validateField = (isValid, formValue, validator, setError) => {
    if (isValid) {
      const validationError = validator(formValue);
      setError(validationError || '');
    }
  };

  // Effect to validate fields when validation state changes (e.g., when Next is clicked)
  useEffect(() => {
    validateField(
      validation.nameValid,
      formData.name,
      validateName,
      setNameError
    );
    validateField(
      validation.pointsValid,
      formData.points,
      validatePoints,
      setPointsError
    );
    validateField(
      validation.orderQtyValid,
      formData.orderQty,
      validateOrderQuantity,
      setOrderQtyError
    );
    validateField(
      validation.limitOfProductQtyValid,
      formData.limitOfProductQty,
      validateBonusProductLimit,
      setLimitOfProductQtyError
    );
    validateField(
      validation.redeemPointsValid,
      formData.redeemPoints,
      validateReedemPoints,
      setRedeemPointsError
    );
    validateField(
      validation.rewardCashAmountValid,
      formData.rewardCashAmount,
      validateRewardCashAmount,
      setRewardCashAmountError
    );
    validateField(
      validation.cardTypeValid,
      formData.cardType,
      validateRewardCardType,
      setCardTypeError
    );
    validateField(
      validation.valueValid,
      formData.value,
      validateValue,
      setValueError
    );

    // Digital specific inline validation when flags are set
    validateField(
      validation.digitalCodeAmountValid,
      formData.digitalCodeAmount,
      validateDigitalValue,
      setDigitalCodeAmountError
    );
    validateField(
      validation.digitalCodePartnerValid,
      formData.digitalCodePartner,
      validateDigitalSupplier,
      setDigitalCodePartnerError
    );
    // Loyalty specific inline validation when flags are set
    validateField(
      validation.loyaltyUrlValid,
      formData.loyaltyUrl,
      validateLoyaltyURL,
      setLoyaltyUrlError
    );
  }, [
    validation.nameValid,
    validation.pointsValid,
    validation.orderQtyValid,
    validation.limitOfProductQtyValid,
    validation.redeemPointsValid,
    validation.rewardCashAmountValid,
    validation.cardTypeValid,
    validation.valueValid,
    formData.name,
    formData.points,
    formData.orderQty,
    formData.limitOfProductQty,
    formData.redeemPoints,
    formData.rewardCashAmount,
    formData.cardType,
    formData.value,
    validateName,
    validatePoints,
    validateOrderQuantity,
    validateBonusProductLimit,
    validateReedemPoints,
    validateRewardCashAmount,
    validateRewardCardType,
    validateValue,
    validateDigitalValue,
    validateDigitalSupplier,
    validation.digitalCodeAmountValid,
    validation.digitalCodePartnerValid,
    formData.digitalCodeAmount,
    formData.digitalCodePartner,
    validateLoyaltyURL,
    validation.loyaltyUrlValid,
    formData.loyaltyUrl
  ]);

  const handleNameChange = useCallback(
    (value) => {
      const validationError = validateName(value);
      setNameError(validationError || '');

      // Always keep formData and validation in sync with local validation
      handleFormChange('name', value);
      handleValidation('name', !!validationError);
    },
    [validateName, handleFormChange, handleValidation]
  );

  const handleResetFormDataFields = (fields) => {
    fields.forEach(({field, value, validate}) => {
      handleFormChange(field, value);
      handleValidation(field, validate);
    });
  };

  const rewardsAttributesReset = [
    {field: 'quota', value: '', validate: false},
    {field: 'leftOverInventory', value: '', validate: false},
    {field: 'limitPerStore', value: '', validate: false},
    {field: 'quotaDigitalCard', value: '', validate: false},
    {field: 'leftOverInventoryDigitalCard', value: '', validate: false},
    {field: 'limitPerStoreDigitalCard', value: '', validate: false},
    {field: 'quotaPhysicalCard', value: '', validate: false},
    {field: 'leftOverInventoryPhysicalCard', value: '', validate: false},
    {field: 'limitPerStorePhysicalCard', value: '', validate: false},
    {field: 'unlimitedInventory', value: false, validate: false}
  ];

  const handleRewardTypeChange = (value) => {
    if (!value || value?.length === 0) return;
    const selectedValue = value?.[0]?.value || '';
    const isBonusProducts = selectedValue === REWARD_TYPES.BONUS_PRODUCTS;

    // Update reward type and validation
    handleFormChange('rewardType', selectedValue);
    handleValidation('rewardType', selectedValue.length === 0);

    // Clear error states
    setPointsError('');
    setOrderQtyError('');
    setLimitOfProductQtyError('');
    setValueError('');

    // Common reset fields
    const commonResets = [
      {field: 'selectedProducts', value: [], validate: false},
      {field: 'selectedPepVouchersProducts', value: [], validate: false},
      {field: 'cardType', value: '', validate: false},
      {field: 'rewardCashAmount', value: '', validate: false},
      {field: 'redeemPoints', value: '', validate: false},
      {field: 'value', value: '', validate: false},
      {field: 'rewardText', value: null, validate: false},
      {field: 'imageUrls', value: [], validate: false},
      {field: 'digitalValue', value: '', validate: false},
      {field: 'digitalSupplier', value: '', validate: false},
      {...rewardsAttributesReset}
    ];

    // Additional resets only if not bonus products
    const nonBonusResets = [
      {field: 'orderQty', value: '', validate: false},
      {field: 'limitOfProductQty', value: '', validate: false}
    ];

    const fieldsToReset = isBonusProducts
      ? commonResets
      : [...commonResets, ...nonBonusResets];

    handleResetFormDataFields(fieldsToReset);
  };

  const handlePointsChange = (value) => {
    const validationError = validatePoints(value);
    setPointsError(validationError || '');

    if (validationError) {
      handleFormChange('redeemPoints', value);
      handleValidation('redeemPoints', true);
    } else {
      handleFormChange('redeemPoints', value);
      handleValidation('redeemPoints', false);
    }
  };

  // Digital specific handlers
  const handleDigitalCodeAmountChange = (value) => {
    const err = !value || Number(value) <= 0 ? 'Enter a valid amount' : '';
    setDigitalCodeAmountError(err);
    handleFormChange('digitalCodeAmount', value);
    handleValidation('digitalCodeAmount', Boolean(err));
  };

  const handleDigitalCodePartnerChange = (value) => {
    handleFormChange('digitalCodePartner', value);
    const err = !value || !String(value).trim() ? 'Supplier is required' : '';
    setDigitalCodePartnerError(err);
    handleValidation('digitalCodePartner', Boolean(err));
  };

  const handleLoyaltyUrlChange = (value) => {
    const validationError = validateLoyaltyURL(value);
    setLoyaltyUrlError(validationError || '');

    handleFormChange('loyaltyUrl', value);
    handleValidation('loyaltyUrl', Boolean(validationError));
  };

  const handleCouponBasedLoyaltyRewardChange = (value) => {
    handleFormChange('couponBasedLoyaltyReward', value);
  };

  const handleAllCustomersChange = useCallback(
    (value) => {
      const err =
        typeof value !== 'boolean' ? 'Value must be true or false' : '';
      handleFormChange('selectedSegments', []);
      handleFormChange('allCustomers', value);
      handleValidation('allCustomers', Boolean(err));
    },
    [handleFormChange, handleValidation]
  );

  const handleSegmentExcludeChange = (value) => {
    const err = typeof value !== 'boolean' ? 'Value must be true or false' : '';
    handleFormChange('excludedSegments', []);
    handleFormChange('segmentExclude', value);
    handleValidation('segmentExclude', Boolean(err));
  };

  const handleSegmentsChange = useCallback(
    (value) => {
      // value is expected to be an array of groups, each a list of ids: [[id,id], [id]]
      const groupedIds = Array.isArray(value) ? value : [];
      handleFormChange('selectedSegments', groupedIds);
    },
    [handleFormChange]
  );

  // New: persist excluded segments (array of ids)
  const handleExcludedSegmentsChange = (value) => {
    const selectedValues = value?.map((item) => item.value) || [];
    handleFormChange('excludedSegments', selectedValues);
  };

  // Helper function to clean up productDisplay for deselected products
  const cleanupProductDisplay = useCallback(
    (selectedProductIds) => {
      const currentProductDisplay = formData?.display?.productDisplay ?? {};
      const updatedProductDisplay = {};
      selectedProductIds.forEach((productId) => {
        if (currentProductDisplay?.[productId]) {
          updatedProductDisplay[productId] = currentProductDisplay[productId];
        }
      });
      return updatedProductDisplay;
    },
    [formData?.display?.productDisplay]
  );

  const handleProductsChange = useCallback(
    (value) => {
      const selectedProducts = value ?? [];
      const selectedProductIds = selectedProducts.map((item) => item.value);
      const updatedProductDisplay = cleanupProductDisplay(selectedProductIds);

      handleFormChange('selectedProducts', selectedProducts);
      handleFormChange('display', {
        ...formData?.display,
        productDisplay: updatedProductDisplay
      });
      handleValidation('selectedProducts', selectedProductIds.length === 0);
    },
    [
      handleFormChange,
      handleValidation,
      formData?.display,
      cleanupProductDisplay
    ]
  );

  const handlePepVouchersChange = useCallback(
    (value) => {
      const selectedPepVouchersProducts = value ?? [];
      const selectedProductIds = selectedPepVouchersProducts.map(
        (item) => item.value
      );
      const updatedProductDisplay = cleanupProductDisplay(selectedProductIds);

      handleFormChange(
        'selectedPepVouchersProducts',
        selectedPepVouchersProducts
      );
      handleFormChange('display', {
        ...formData?.display,
        productDisplay: updatedProductDisplay
      });
      handleValidation(
        'selectedPepVouchersProducts',
        selectedProductIds.length === 0
      );
    },
    [
      handleFormChange,
      handleValidation,
      formData?.display,
      cleanupProductDisplay
    ]
  );

  const handleOrderQtyChange = (value) => {
    const validationError = validateOrderQuantity(value);
    setOrderQtyError(validationError || '');

    if (validationError) {
      handleFormChange('orderQty', value);
      handleValidation('orderQty', true);
    } else {
      handleFormChange('orderQty', value);
      handleValidation('orderQty', false);
    }
  };

  const handleRedeemPointsChange = (value) => {
    const validationError = validateReedemPoints(value);
    setRedeemPointsError(validationError || '');

    if (validationError) {
      handleFormChange('redeemPoints', value);
      handleValidation('redeemPoints', true);
    } else {
      handleFormChange('redeemPoints', value);
      handleValidation('redeemPoints', false);
    }
  };

  const handleRewardCashAmountChange = (value) => {
    const validationError = validateRewardCashAmount(value);
    setRewardCashAmountError(validationError || '');

    if (validationError) {
      handleFormChange('rewardCashAmount', value);
      handleValidation('rewardCashAmount', true);
    } else {
      handleFormChange('rewardCashAmount', value);
      handleValidation('rewardCashAmount', false);
    }
  };

  const handleCardTypeChange = (value) => {
    const validationError = validateRewardCardType(value[0]?.value);
    setCardTypeError(validationError || '');

    if (validationError) {
      handleFormChange('cardType', value[0]?.value);
      handleValidation('cardType', true);
    } else {
      handleFormChange('cardType', value[0]?.value);
      handleValidation('cardType', false);
    }
    handleResetFormDataFields(rewardsAttributesReset);
  };

  const handleLimitOfProductQtyChange = (value) => {
    const validationError = validateBonusProductLimit(value);
    setLimitOfProductQtyError(validationError || '');

    if (validationError) {
      handleFormChange('limitOfProductQty', value);
      handleValidation('limitOfProductQty', true);
    } else {
      handleFormChange('limitOfProductQty', value);
      handleValidation('limitOfProductQty', false);
    }
  };

  // Used for DISCOUNT/PEP_VOUCHER_DISCOUNT types (CASH uses handleRewardCashAmountChange)
  const handleValueChange = (value) => {
    const validationError = validateValue(value);
    setValueError(validationError || '');

    if (validationError) {
      handleFormChange('value', value);
      handleValidation('value', true);
    } else {
      handleFormChange('value', value);
      handleValidation('value', false);
    }
  };

  const handleResetRewardConditions = () => {
    // Call the original reset function
    resetRewardConditions();

    // Clear all local error states
    setPointsError('');
    setOrderQtyError('');
    setLimitOfProductQtyError('');
    setRedeemPointsError('');
    setCardTypeError('');
    setRewardCashAmountError('');
    setValueError('');
    setDigitalCodeAmountError('');
    setDigitalCodePartnerError('');
    setLoyaltyUrlError('');
  };

  // Handle products update from ProductsCard
  const handleUpdateProducts = useCallback(
    (updatedProducts) => {
      handleFetchedProductsUpdate(updatedProducts);
    },
    [handleFetchedProductsUpdate]
  );

  // Handle product deletion
  const handleDeleteProduct = useCallback(
    (productId) => {
      // Remove from selected products
      const updatedSelectedProducts = formData.selectedProducts.filter(
        (product) => product.value !== productId
      );
      handleFormChange('selectedProducts', updatedSelectedProducts);

      // Remove from fetched products while preserving details for remaining products
      const updatedFetchedProducts = formData.fetchedProducts.filter(
        (product) => product.productId !== productId
      );
      handleFetchedProductsUpdate(updatedFetchedProducts);

      // Remove from display.productDisplay to keep display data in sync
      if (formData?.display?.productDisplay?.[productId]) {
        const remainingProductDisplay = Object.fromEntries(
          Object.entries(formData.display.productDisplay).filter(
            ([key]) => key !== productId
          )
        );
        handleFormChange('display', {
          ...formData.display,
          productDisplay: remainingProductDisplay
        });
      }
    },
    [
      formData.selectedProducts,
      formData.fetchedProducts,
      formData.display,
      handleFormChange,
      handleFetchedProductsUpdate
    ]
  );

  const showProductsCard =
    formData.selectedProducts &&
    formData.selectedProducts.length > 0 &&
    formData?.rewardType === REWARD_TYPES.BONUS_PRODUCTS;
  const showDigitalCodesCard =
    formData?.rewardType === REWARD_TYPES.DIGITAL ||
    formData?.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER ||
    (formData?.rewardType === REWARD_TYPES.LOYALTY &&
      formData?.couponBasedLoyaltyReward === true);

  // For reward types that require digital codes upload (DIGITAL, DIGITAL_PRE_ORDER, LOYALTY with coupon),
  // disable Name field in edit mode. Reward type has an additional duplicate-flow lock.
  const isDigitalCodeRewardType =
    formData?.rewardType === REWARD_TYPES.DIGITAL ||
    formData?.rewardType === REWARD_TYPES.DIGITAL_PRE_ORDER ||
    formData?.rewardType === REWARD_TYPES.LOYALTY;
  const isEditMode = mode === 'edit' || !!formData?.rewardId;
  const shouldDisableName = isEditMode && isDigitalCodeRewardType;
  const shouldDisableRewardType =
    shouldDisableName || !!formData?.isDuplicateFlow;

  return (
    <div className='rewardSetup-container flex flex-col gap-6'>
      <RewardSetupCard
        formData={formData}
        handleNameChange={handleNameChange}
        nameError={nameError}
        isNameDisabled={shouldDisableName}
      />

      <RewardConditionsCard
        formData={formData}
        validation={validation}
        rewardTypes={rewardTypes}
        rewardCardTypes={rewardCardTypes}
        rewardTypesLoading={rewardTypesLoading}
        bonusProductsLoading={bonusProductsLoading}
        rewardCardTypesLoading={rewardCardTypesLoading}
        pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
        handleRewardTypeChange={handleRewardTypeChange}
        handlePointsChange={handlePointsChange}
        handleProductsChange={handleProductsChange}
        handleOrderQtyChange={handleOrderQtyChange}
        handleRedeemPointsChange={handleRedeemPointsChange}
        handleRewardCashAmountChange={handleRewardCashAmountChange}
        handleCardTypeChange={handleCardTypeChange}
        handleDigitalCodeAmountChange={handleDigitalCodeAmountChange}
        handleDigitalCodePartnerChange={handleDigitalCodePartnerChange}
        handleLimitOfProductQtyChange={handleLimitOfProductQtyChange}
        handleValueChange={handleValueChange}
        handleLoyaltyUrlChange={handleLoyaltyUrlChange}
        handleCouponBasedLoyaltyRewardChange={
          handleCouponBasedLoyaltyRewardChange
        }
        resetRewardConditions={handleResetRewardConditions}
        handlePepVouchersChange={handlePepVouchersChange}
        pointsError={pointsError}
        orderQtyError={orderQtyError}
        limitOfProductQtyError={limitOfProductQtyError}
        rewardCashAmountError={rewardCashAmountError}
        redeemPointsError={redeemPointsError}
        digitalCodeAmountError={digitalCodeAmountError}
        digitalCodePartnerError={digitalCodePartnerError}
        cardTypeError={cardTypeError}
        valueError={valueError}
        loyaltyUrlError={loyaltyUrlError}
        isRewardTypeDisabled={shouldDisableRewardType}
      />

      {/* Products Card - Show when we have selected products */}
      {showProductsCard && (
        <ProductsCard
          products={formData.fetchedProducts}
          productsLoading={productsLoading}
          onUpdateProducts={handleUpdateProducts}
          onDeleteProduct={handleDeleteProduct}
          isReadOnly={false}
          rewardStatus={formData.status}
        />
      )}

      {showDigitalCodesCard && <DigitalCodesCard onSaveDraft={onSaveDraft} />}

      <SegmentsCard
        formData={formData}
        segments={segments}
        segmentsLoading={segmentsLoading}
        handleAllCustomersChange={handleAllCustomersChange}
        handleSegmentExcludeChange={handleSegmentExcludeChange}
        handleSegmentsChange={handleSegmentsChange}
        handleExcludedSegmentsChange={handleExcludedSegmentsChange}
        isReadOnly={false}
      />
    </div>
  );
};

RewardSetup.propTypes = {
  onSaveDraft: PropTypes.func,
  mode: PropTypes.oneOf(['create', 'edit'])
};

export default RewardSetup;
